import { useSearchParams } from 'react-router';

/**
 * URL-`?tab=`-driven tab state. The URL query param is the single source of
 * truth — the active value is derived from it (no local `useState`/`useEffect`
 * mirror), so deep links, back/forward, and external writes stay consistent.
 *
 * Single-concern primitive: callers compose `startTransition` and any default
 * inference themselves (the resolved `defaultValue` is passed in). `setValue`
 * writes the param via the callback form so sibling query params are preserved.
 *
 * @example
 * const [tab, setTab] = useTabParam({
 *   validValues: ['reports', 'loa', 'files'],
 *   defaultValue: 'reports',
 * });
 */
export function useTabParam(opts: {
  validValues: readonly string[];
  defaultValue: string;
  param?: string;
  replace?: boolean;
}): [string, (next: string) => void] {
  const { validValues, defaultValue, param = 'tab', replace = true } = opts;
  const [searchParams, setSearchParams] = useSearchParams();

  // Derived during render; the React Compiler memoizes this automatically
  // (cheap: one URLSearchParams.get + Array.includes over a tiny array).
  const urlValue = searchParams.get(param);
  const value =
    urlValue !== null && validValues.includes(urlValue)
      ? urlValue
      : defaultValue;

  const setValue = (next: string) => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);
        params.set(param, next);
        return params;
      },
      { replace }
    );
  };

  return [value, setValue];
}
